import type { OperatingParameters } from "@/composables/forms/formTypes";
import { useScenarioCalculations } from "@/composables/scenarioCalculations/useScenarioCalculations";
import type { DestructuredScenario } from "@/composables/useDestructureScenario";
import { ref } from "vue";
import type { SinglePumpChartHypotheticals } from "../tornado";

export const getHypotheticalScenarioCalculations = (
  destructuredScenario: DestructuredScenario,
  graphMax: number,
  pumpIntakeFlowRateBBLD: number
): SinglePumpChartHypotheticals => {
  const originalTargetLiquidFlowRateSTBD =
    destructuredScenario.parameters.value?.target_liquid_rate_std_barrel_day ||
    0;

  const hypotheticalParameters = ref<NonNullable<OperatingParameters>>({
    ...destructuredScenario.parameters.value,
    __typename: "scenario_parameters"
  });

  const hypotheticalCalcs = useScenarioCalculations({
    ...destructuredScenario,
    parameters: hypotheticalParameters
  });

  // We are explicitly adding the target liquid flow rate and the pump intake flow rate
  // as hypothetical points to ensure they are included in the chart,
  // even if they are outside the evenly spaced hypothetical flow rates.
  // This is important for anchoring the chart to real data points.
  // Target liquid rate is surface which is what we used to plot, but now we plot
  // in situ rates so add the pump intake flow rate for the specified pump
  const hypotheticalFlowRates = [
    ...Array.from({ length: 40 }, (_, i) => (graphMax / 40) * (i + 1)),
    originalTargetLiquidFlowRateSTBD,
    pumpIntakeFlowRateBBLD
  ].sort((a, b) => a - b);

  return {
    hypotheticalCalcs: hypotheticalFlowRates.map(liquidFlowRateSTBD => {
      Object.assign(hypotheticalParameters.value || {}, {
        target_liquid_rate_std_barrel_day: liquidFlowRateSTBD
      });

      // Technically liquidFlowRateSTBD is just a flow rate number here
      // since we're updating target_liquid_rate_std_barrel_day above
      // we call it STBD
      return {
        liquidFlowRateSTBD,
        producedTotalDynamicHeadFt:
          hypotheticalCalcs.pumpCalculations.aggregate
            .producedTotalDynamicHeadFt.value,
        requiredPumpBrakeHP:
          hypotheticalCalcs.pumpCalculations.aggregate.requiredPumpBrakeHP
            .value,
        pumpEfficiencyRatio:
          hypotheticalCalcs.pumpCalculations.aggregate.pumpEfficiencyRatio
            .value,
        requiredTotalDynamicHeadFt:
          hypotheticalCalcs.pumpCalculations.aggregate
            .requiredTotalDynamicHeadFt.value,
        requiredDifferentialPressurePSIA:
          hypotheticalCalcs.pumpCalculations.aggregate.differentialPressurePSIA
            .value,
        pumpIntakeFlowRateBBLD:
          hypotheticalCalcs.pumpCalculations.aggregate.intakeFlowRateSTBD.value,
        pumpDischargeFlowRateBBLD:
          hypotheticalCalcs.pumpCalculations.aggregate.dischargeFlowRateSTBD
            .value
      };
    }),
    hypotheticalFlowRates
  };
};
